import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import css from './DiscoverTheBeauty.module.css';
import Slider from 'react-slick';
import { IconArrowHead } from '../../../components';
import { ListingCard } from '../../../components';
import { fetchCarouselListings } from '../../../containers/SearchPage/SearchPage.duck';
import { getListingsById } from '../../../ducks/marketplaceData.duck';
import { useConfiguration } from '../../../context/configurationContext';

// Define empty array outside component to prevent recreation
const EMPTY_ARRAY = [];

export default function DiscoverTheBeauty() {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const [selectedTab, setSelectedTab] = useState('hairChair');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const sliderRef = useRef(null);

  const { carouselListings } = useSelector(state => state.SearchPage);
  
  // Memoize the listing IDs to prevent unnecessary selector calls
  const currentListingIds = useMemo(() => {
    return carouselListings[selectedTab] || EMPTY_ARRAY;
  }, [carouselListings, selectedTab]);

  // Alternative approach: Use a callback selector with shallow comparison
  const selectListings = useCallback((state) => {
    if (currentListingIds.length === 0) return EMPTY_ARRAY;
    return getListingsById(state, currentListingIds);
  }, [currentListingIds]);

  const listings = useSelector(selectListings, (left, right) => {
    // Custom equality check - compare by reference first, then by length and content
    if (left === right) return true;
    if (!left || !right) return false;
    if (left.length !== right.length) return false;
    
    // If both are empty arrays, they're equal
    if (left.length === 0 && right.length === 0) return true;
    
    // Compare each item by id
    return left.every((item, index) => {
      const rightItem = right[index];
      return item && rightItem && item.id && rightItem.id &&
        item.id.uuid === rightItem.id.uuid;
    });
  });

  useEffect(() => {
    if (!carouselListings[selectedTab]) {
      dispatch(fetchCarouselListings(selectedTab, config));
    }
  }, [selectedTab, dispatch, config, carouselListings]);

  // Update slidesToShow based on window size
  useEffect(() => {
    const updateSlidesToShow = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setSlidesToShow(1.1);
      } else if (width <= 768) {
        setSlidesToShow(2);
      } else if (width <= 1200) {
        setSlidesToShow(3);
      } else {
        setSlidesToShow(4);
      }
    };

    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);

  const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    // speed: 2000,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 3000,
    // cssEase: 'ease-in-out',
    pauseOnHover: false,
    swipeToSlide: false,
    centerMode: false,

    responsive: [
      {
        breakpoint: 1200,
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1.1 },
      },
    ],
  };

  // Filter data based on selected tab
  const filteredData = listings;

  // Calculate if arrows should be disabled with proper logic
  const isPrevDisabled = currentSlide === 0;
  const maxSlideIndex = Math.max(0, filteredData.length - Math.floor(slidesToShow));
  const mobileMaxSlideIndex = Math.max(0, filteredData.length - slidesToShow);
  const isNextDisabled = currentSlide >= maxSlideIndex || filteredData.length <= slidesToShow || currentSlide >= mobileMaxSlideIndex;

  const categories = config.categoryConfiguration.categories;
  const tabs = categories.map(cat => ({
    id: cat.id,
    text: cat.name,
    selected: selectedTab === cat.id,
  }));

  const handleTabClick = tabId => {
    setSelectedTab(tabId);
    setCurrentSlide(0); // Reset to first slide when changing tabs
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  };

  const handlePrevSlide = () => {
    if (sliderRef.current && !isPrevDisabled) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNextSlide = () => {
    if (sliderRef.current && !isNextDisabled) {
      sliderRef.current.slickNext();
    }
  };

  const handleBeforeChange = (oldIndex, newIndex) => {
    setCurrentSlide(newIndex);
  };

  const hasListings = filteredData && filteredData.length > 0;

  return (
    <>
      <div className={css.carouselContainer}>
        <div className={css.tabContainer}>
          <div className={css.tabNav}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`${css.tabItem} ${tab.selected ? css.selectedTab : ''}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.text}
              </button>
            ))}
          </div>
          {hasListings ? (
            <div className={css.navigationArrows}>
              <button
                className={`${css.arrowButton} ${isPrevDisabled ? css.disabled : ''}`}
                onClick={handlePrevSlide}
                disabled={isPrevDisabled}
              >
                <IconArrowHead direction="left" size="small" />
              </button>
              <button
                className={`${css.arrowButton} ${isNextDisabled ? css.disabled : ''}`}
                onClick={handleNextSlide}
                disabled={isNextDisabled}
              >
                <IconArrowHead direction="right" size="small" />
              </button>
            </div>
          ) : null}
        </div>
        <div>
          {hasListings ? (
            <Slider
              ref={sliderRef}
              {...settings}
              className={css.carousel}
              beforeChange={handleBeforeChange}
            >
              {filteredData.map(item => (
                <ListingCard key={item.id.uuid} listing={item} showAuthorInfo={false} />
              ))}
            </Slider>
          ) : (
            <div className={css.noListings}>
              <p><FormattedMessage id="DiscoverTheBeauty.noListings" /></p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}